import { isLscSurface } from './routing';
import { isLegalPath } from '../legal/legalLinks';

export const STREAM_CENTER_DOCUMENT_TITLE = 'Stream Center by Scoutlytics';
export const STREAM_CENTER_LANDING_DOCUMENT_TITLE =
  'League Stream Center — Turn your league streams into a media hub';
export const SCOUTLYTICS_DOCUMENT_TITLE = 'Scoutlytics - AI-Powered Madden Scouting';

export const STREAM_CENTER_FAVICON_BASE = '/assets/stream-center';
export const SCOUTLYTICS_FAVICON_BASE = '';
export const STREAM_CENTER_MANIFEST_HREF = '/manifest-stream-center.json';
export const SCOUTLYTICS_MANIFEST_HREF = '/manifest.json';

export const STREAM_CENTER_APPLE_WEB_APP_TITLE = 'Stream Center';

export type AppFaviconProfile = 'scoutlytics' | 'stream-center';

const FAVICON_MANAGED_ATTR = 'data-app-favicon';
const PWA_MANAGED_ATTR = 'data-app-pwa-meta';
const MANIFEST_LINK_ID = 'app-manifest-link';

interface PwaMetaSpec {
  id: string;
  name: string;
  content: string;
}

const STREAM_CENTER_PWA_META: PwaMetaSpec[] = [
  { id: 'app-pwa-apple-capable', name: 'apple-mobile-web-app-capable', content: 'yes' },
  {
    id: 'app-pwa-apple-title',
    name: 'apple-mobile-web-app-title',
    content: STREAM_CENTER_APPLE_WEB_APP_TITLE,
  },
  {
    id: 'app-pwa-apple-status-bar',
    name: 'apple-mobile-web-app-status-bar-style',
    content: 'black-translucent',
  },
];

interface FaviconLinkSpec {
  id: string;
  rel: string;
  href: string;
  sizes?: string;
  type?: string;
}

const STREAM_CENTER_FAVICON_LINKS: FaviconLinkSpec[] = [
  {
    id: 'app-favicon-stream-center-ico',
    rel: 'icon',
    href: `${STREAM_CENTER_FAVICON_BASE}/favicon.ico`,
    sizes: 'any',
  },
  {
    id: 'app-favicon-stream-center-16',
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
    href: `${STREAM_CENTER_FAVICON_BASE}/favicon-16x16.png`,
  },
  {
    id: 'app-favicon-stream-center-32',
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: `${STREAM_CENTER_FAVICON_BASE}/favicon-32x32.png`,
  },
  {
    id: 'app-favicon-stream-center-48',
    rel: 'icon',
    type: 'image/png',
    sizes: '48x48',
    href: `${STREAM_CENTER_FAVICON_BASE}/favicon-48x48.png`,
  },
  {
    id: 'app-favicon-stream-center-shortcut',
    rel: 'shortcut icon',
    href: `${STREAM_CENTER_FAVICON_BASE}/favicon.ico`,
  },
  {
    id: 'app-favicon-stream-center-apple',
    rel: 'apple-touch-icon',
    href: `${STREAM_CENTER_FAVICON_BASE}/apple-touch-icon.png`,
  },
];

const SCOUTLYTICS_FAVICON_LINKS: FaviconLinkSpec[] = [
  {
    id: 'app-favicon-scoutlytics-ico',
    rel: 'icon',
    href: `${SCOUTLYTICS_FAVICON_BASE}/favicon.ico`,
    sizes: 'any',
  },
  {
    id: 'app-favicon-scoutlytics-32',
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: `${SCOUTLYTICS_FAVICON_BASE}/favicon-32.png`,
  },
  {
    id: 'app-favicon-scoutlytics-shortcut',
    rel: 'shortcut icon',
    href: `${SCOUTLYTICS_FAVICON_BASE}/favicon.ico`,
  },
  {
    id: 'app-favicon-scoutlytics-apple',
    rel: 'apple-touch-icon',
    href: `${SCOUTLYTICS_FAVICON_BASE}/apple-touch-icon.png`,
  },
];

let activeProfile: AppFaviconProfile | null = null;

function upsertFaviconLink(spec: FaviconLinkSpec, profile: AppFaviconProfile): HTMLLinkElement {
  const selector = `link#${spec.id}`;
  let link = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement('link');
    link.id = spec.id;
    document.head.appendChild(link);
  }

  link.setAttribute(FAVICON_MANAGED_ATTR, profile);
  link.rel = spec.rel;
  link.href = spec.href;
  if (spec.sizes) {
    link.setAttribute('sizes', spec.sizes);
  } else {
    link.removeAttribute('sizes');
  }
  if (spec.type) {
    link.type = spec.type;
  } else {
    link.removeAttribute('type');
  }

  return link;
}

function removeUnmanagedFaviconLinks(): void {
  const managedIds = new Set(
    [...STREAM_CENTER_FAVICON_LINKS, ...SCOUTLYTICS_FAVICON_LINKS].map((spec) => spec.id)
  );

  document.head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((node) => {
    const link = node as HTMLLinkElement;
    if (!managedIds.has(link.id)) {
      link.remove();
    }
  });
}

function upsertManifestLink(href: string, profile: AppFaviconProfile): void {
  let link = document.head.querySelector(`#${MANIFEST_LINK_ID}`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = MANIFEST_LINK_ID;
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  link.setAttribute(PWA_MANAGED_ATTR, profile);
  link.href = href;
}

function upsertPwaMeta(spec: PwaMetaSpec, profile: AppFaviconProfile): HTMLMetaElement {
  const selector = `meta#${spec.id}`;
  let meta = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.id = spec.id;
    document.head.appendChild(meta);
  }
  meta.setAttribute(PWA_MANAGED_ATTR, profile);
  meta.name = spec.name;
  meta.content = spec.content;
  return meta;
}

function removeStreamCenterPwaMeta(): void {
  document.head.querySelectorAll(`meta[${PWA_MANAGED_ATTR}="stream-center"]`).forEach((node) => {
    node.remove();
  });
}

function applyStreamCenterPwaMeta(): void {
  upsertManifestLink(STREAM_CENTER_MANIFEST_HREF, 'stream-center');
  STREAM_CENTER_PWA_META.forEach((spec) => upsertPwaMeta(spec, 'stream-center'));
}

function applyScoutlyticsPwaMeta(): void {
  upsertManifestLink(SCOUTLYTICS_MANIFEST_HREF, 'scoutlytics');
  removeStreamCenterPwaMeta();
}

export function applyStreamCenterHead(title = STREAM_CENTER_DOCUMENT_TITLE): void {
  if (activeProfile === 'stream-center' && document.title === title) {
    return;
  }

  removeUnmanagedFaviconLinks();
  STREAM_CENTER_FAVICON_LINKS.forEach((spec) => upsertFaviconLink(spec, 'stream-center'));
  applyStreamCenterPwaMeta();
  document.title = title;
  activeProfile = 'stream-center';
}

export function restoreScoutlyticsHead(): void {
  if (activeProfile === 'scoutlytics') {
    document.title = SCOUTLYTICS_DOCUMENT_TITLE;
    return;
  }

  removeUnmanagedFaviconLinks();
  SCOUTLYTICS_FAVICON_LINKS.forEach((spec) => upsertFaviconLink(spec, 'scoutlytics'));
  applyScoutlyticsPwaMeta();
  document.title = SCOUTLYTICS_DOCUMENT_TITLE;
  activeProfile = 'scoutlytics';
}

function isLscLandingPath(pathname?: string): boolean {
  if (!pathname) return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return normalized === '/' || normalized === '/lsc';
}

export function syncDocumentHead(hostname?: string, pathname?: string): AppFaviconProfile {
  if (pathname && isLegalPath(pathname)) {
    restoreScoutlyticsHead();
    return 'scoutlytics';
  }
  if (isLscSurface(hostname, pathname)) {
    applyStreamCenterHead(
      isLscLandingPath(pathname)
        ? STREAM_CENTER_LANDING_DOCUMENT_TITLE
        : STREAM_CENTER_DOCUMENT_TITLE
    );
    return 'stream-center';
  }
  restoreScoutlyticsHead();
  return 'scoutlytics';
}

export function getActiveFaviconProfile(): AppFaviconProfile | null {
  return activeProfile;
}

export function getManagedFaviconHref(profile: AppFaviconProfile, rel = 'icon', sizes?: string): string | null {
  const selector = sizes
    ? `link[${FAVICON_MANAGED_ATTR}="${profile}"][rel="${rel}"][sizes="${sizes}"]`
    : `link[${FAVICON_MANAGED_ATTR}="${profile}"][rel="${rel}"]`;
  const link = document.head.querySelector(selector) as HTMLLinkElement | null;
  return link?.getAttribute('href') ?? null;
}

/** Reset module state for tests. */
export function getManagedManifestHref(): string | null {
  const link = document.head.querySelector(`#${MANIFEST_LINK_ID}`) as HTMLLinkElement | null;
  return link?.getAttribute('href') ?? null;
}

export function getManagedPwaMetaContent(name: string): string | null {
  const meta = document.head.querySelector(
    `meta[${PWA_MANAGED_ATTR}="stream-center"][name="${name}"]`
  ) as HTMLMetaElement | null;
  return meta?.getAttribute('content') ?? null;
}

/** Reset module state for tests. */
export function resetDocumentHeadState(): void {
  activeProfile = null;
  document.head
    .querySelectorAll(`link[${FAVICON_MANAGED_ATTR}]`)
    .forEach((node) => node.remove());
  document.head.querySelector(`#${MANIFEST_LINK_ID}`)?.remove();
  document.head.querySelectorAll(`meta[${PWA_MANAGED_ATTR}]`).forEach((node) => node.remove());
}
